import React, { useState } from 'react';
import { useWeb3React } from '@web3-react/core';
import useModalState from '../../context/modal/useModalState';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import clsx from 'clsx';
import useUserCreatedReferral from '../../hooks/referral/useUserCreatedReferral';
import useErrorState from '../../context/error/useErrorState';
import { createReferral, getReferralById, signTypedData } from '../../helpers';
import {
    EXISTING_REFERRAL,
    REFERRAL_CREATED,
    REFERRAL_MIN_LENGTH,
    SIGN_REFERRAL,
    VERIFY_REFERRAL,
} from '../../config/constants';
import useToastState from '../../context/toast/useToastState';
import { TypedDataDomain, TypedDataField } from 'ethers';

//eslint-disable-next-line
const specialChars = /[`!@#$%^&*()_+\- =\[\]{};':"\\|,.<>\/?~]/;

const ReferralCreator = () => {
    const { openModal, closeModal, setModalData } = useModalState();

    const { setToast } = useToastState();

    const { setError } = useErrorState();

    const { account, library } = useWeb3React();

    const { mutate } = useUserCreatedReferral(account);

    const [isExistingCode, setIsExistingCode] = useState(false);
    const [referralCode, setReferralCode] = useState('');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isExistingCode) {
            setIsExistingCode(false);
        }

        const { value } = e.target;

        if (specialChars.test(value)) return;

        setReferralCode(value);
    };

    const verifyExistingReferral = async () => {
        openModal('waitingModal');
        setModalData((prev) => ({
            ...prev,
            status: VERIFY_REFERRAL,
        }));

        const referral = await getReferralById(referralCode);

        return referral.status !== 404;
    };

    const create = async () => {
        try {
            if (!account) return;

            if (referralCode.length < 6) {
                setToast(
                    { type: 'danger', message: REFERRAL_MIN_LENGTH },
                    REFERRAL_MIN_LENGTH
                );
                return;
            }

            const referralExists = await verifyExistingReferral();

            if (referralExists) {
                closeModal('waitingModal');
                setToast(
                    { type: 'danger', message: EXISTING_REFERRAL },
                    EXISTING_REFERRAL
                );

                setIsExistingCode(true);
                return;
            }

            setModalData((prev) => ({
                ...prev,
                status: SIGN_REFERRAL,
                message: '',
            }));

            const domain: TypedDataDomain = {
                name: 'UnlimitedReferral',
                version: '1',
            };

            const message = {
                code: referralCode,
            };

            const types: Record<string, TypedDataField[]> = {
                EIP712Domain: [
                    { name: 'name', type: 'string' },
                    { name: 'version', type: 'string' },
                ],
                'Unlimited Referrals': [
                    {
                        name: 'code',
                        type: 'string',
                    },
                ],
            };

            const typedOrder = JSON.stringify({
                domain,
                message,
                types,
                primaryType: 'Unlimited Referrals',
            });

            const signature = await signTypedData(library, account, typedOrder);

            await createReferral({ code: referralCode, signature, address: account });

            await mutate();

            setToast(
                {
                    type: 'success',
                    message: REFERRAL_CREATED,
                },
                REFERRAL_CREATED
            );
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    };

    return (
        <div className='custom-referral-code'>
            <p>Create your own customized referral code (Max 16 characters)</p>
            <div className='referral-code-wrapper'>
                <label className='input-container' htmlFor='custom-referral-code'>
                    <div className='truncate'>{window.location.hostname}?</div>
                    <div className='non-truncate'>
                        <span>refCode=</span>
                        <TextInput
                            id='custom-referral-code'
                            className='code'
                            name='custom-referral-code'
                            variant='text'
                            value={referralCode}
                            validate={(value) => value.length <= 16}
                            onChange={handleChange}
                            placeholder='referral-code'
                            onKeyUp={(e) => e.key === 'Enter' && create()}
                        />
                    </div>
                </label>
                <Button className='create' onClick={create} disabled={!referralCode}>
                    Create
                </Button>
            </div>
            <div className={clsx('error-message', { visible: isExistingCode })}>
                This code alerady exists, please try something else
            </div>
        </div>
    );
};

export default ReferralCreator;
